import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTemplateContext, useProperty } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// Note: the image field intentionally has no defaultField. We chain the
// fallback manually below so the hyperlocal item's featured_image takes
// priority over the property's featured image. With a defaultField here,
// `bgImage.image` would always resolve to property_featured_image and
// the per-item image could never surface.
const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page hero image' },
  },
};

// Escape user-supplied plain text before injecting it into a richtext default.
// Hyperlocal h1_title / h2_subtitle are plain strings; wrap them in <p> so
// RichTextContent's HTML pipeline keeps the same DOM shape, and neutralize
// any HTML special characters so a user-typed `<` doesn't poison the markup.
function toRichTextDefault(text: string | undefined | null, fallback: string): string {
  if (!text) return fallback;
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${escaped}</p>`;
}

// The hyperlocal generator stores "no value" as the literal strings "false",
// "null", "none", or empty — none of which are valid image URLs but all are
// truthy in JS. Treat them as missing so the fallback chain advances.
function pickImage(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (lower === 'false' || lower === 'true' || lower === 'null' || lower === 'none' || lower === 'undefined') return null;
  return trimmed;
}

export function HyperlocalHero() {
  const ctx = useTemplateContext();
  const hl = ctx?.hyperlocal as Record<string, string | null | undefined> | undefined;
  const property = useProperty();

  const bgImage = useSlotGroup('hyperlocalhero.bg_image', bgImageDef);

  // Image priority: explicit slot-group override > hyperlocal featured_image >
  // property featured image > nothing. pickImage() filters out generator-emitted
  // sentinel strings ("false", "null", etc.) so the fallback advances cleanly.
  const displayImage =
    pickImage(bgImage.image) ||
    pickImage(hl?.featured_image) ||
    pickImage((property as Record<string, any>)?.property_featured_image) ||
    '';
  const displayAlt = bgImage.alt || hl?.h1_title || 'Page hero image';

  const titleDefault = toRichTextDefault(hl?.h1_title, '<p>Hyperlocal Page Title</p>');
  const subtitleDefault = toRichTextDefault(hl?.h2_subtitle, '<p>Explore apartments in your area</p>');

  return (
    <section className="relative w-full h-[70vh] min-h-[450px] max-h-[700px] overflow-hidden">
      <EditableSlotGroup groupPath="hyperlocalhero.bg_image" definition={bgImageDef}>
        <div className="absolute inset-0">
          {displayImage ? (
            <VibesitesImage
              url={displayImage}
              alt={displayAlt}
              priority
              className="w-full h-full object-cover"
              style={{
                animation: 'hlKenBurns 30s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'bottom right',
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8">
        <RichTextContent
          path="hyperlocalhero.title"
          label="Title"
          defaultValue={titleDefault}
          as="h1"
          className="hl-entrance hl-entrance-1 font-heading text-4xl sm:text-5xl md:text-6xl font-normal mb-5 max-w-5xl leading-[1.1] tracking-tight drop-shadow-lg"
        />
        <RichTextContent
          path="hyperlocalhero.subtitle"
          label="Subtitle"
          defaultValue={subtitleDefault}
          as="h2"
          className="hl-entrance hl-entrance-2 font-body text-lg sm:text-xl md:text-2xl max-w-3xl leading-relaxed opacity-90 drop-shadow-md font-normal"
        />
      </div>

      <style>{`
        @keyframes hlKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes hlEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hl-entrance {
          animation: hlEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hl-entrance-1 { animation-delay: 0.2s; }
        .hl-entrance-2 { animation-delay: 0.45s; }
        @media (prefers-reduced-motion: reduce) {
          .hl-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HyperlocalHero',
  displayName: 'Hyperlocal Hero',
  tokens: {
    'hyperlocalhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Hyperlocal Page Title</p>',
    },
    'hyperlocalhero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Explore apartments in your area</p>',
    },
  },
  slotGroups: {
    'hyperlocalhero.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultValue: '' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page hero image' },
      },
    },
  },
};

(HyperlocalHero as any).contentSchema = contentSchema;

export default HyperlocalHero;
